import { Link } from "react-router-dom";
import { Children, isValidElement } from "react";
import MarketingLayout from "@/components/MarketingLayout";
import Seo from "@/components/Seo";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { ArrowRight } from "@phosphor-icons/react";

const GROUPS = [
  {
    tag: "Getting started",
    faqs: [
      ["What is WorkWithLottie?", "Lottie is an intelligent month-end assistant for Xero. She connects directly to your Xero organisation and automatically manages your accruals, prepayments and deferred income — identifying the right transactions, building the schedules, posting the journals and keeping your registers up to date."],
      ["Who is it for?", "Accountants, bookkeepers and finance teams who use Xero and want to take the manual effort and spreadsheet risk out of month-end — whether you look after a single business or a whole portfolio of client organisations."],
      ["How do I get started?", "Start your 7-day free trial, connect your Xero organisation, and tell Lottie which nominal codes hold your accruals, prepayments and deferred income. From there she builds your registers and keeps them running. Setup usually takes only a few minutes."],
      ["Do I need a Xero account?", "Yes — Lottie is built specifically for Xero and works by connecting to your Xero organisation(s). You connect securely using your existing Xero login."],
    ],
  },
  {
    tag: "Control & confidence",
    faqs: [
      ["Can I review journals before they post?", "Yes — always. Every proposed journal can be reviewed in full before anything reaches Xero. You can hold all postings behind approval so nothing moves without a person signing it off."],
      ["Can I amend schedules?", "Yes. You can adjust the release basis, period, amounts or start date on any schedule whenever the facts change, and Lottie recalculates the remaining journals automatically."],
      ["Does Lottie keep an audit trail?", "Yes. Every schedule, edit, approval and posting is logged with the user and timestamp, giving you a complete, exportable audit trail behind every figure."],
      ["Does Lottie make the accounting decisions for me?", "No. Lottie automates the repetitive administration — identifying transactions, building schedules and posting the journals you approve. The accounting treatment and professional judgement always remain yours."],
      ["Can multiple staff use it?", "Yes. Preparers and reviewers work together with review notes and a two-person approval control, so month-end can be prepared and signed off by different members of the team."],
      ["Can I manage multiple Xero organisations?", "Yes. Manage as many Xero organisations as you need from one portal, each with its own isolated chart of accounts, settings and registers — ideal for practices standardising month-end across clients."],
      ["What Xero permissions are required?", "Lottie connects through Xero's official OAuth. It reads your invoices and bills, chart of accounts and trial-balance figures — plus invoice attachments and your fixed-asset register where used — and it posts the manual journals and reversals that run your accruals, prepayments and deferred income. It never edits your invoices, bank transactions or contacts. Journals can post automatically or be held behind two-person approval, Xero shows you every permission at connection, and you can disconnect at any time."],
    ],
  },
  {
    tag: "Xero & your data",
    faqs: [
      ["How does Lottie connect to Xero?", "Through Xero's official, secure OAuth connection. You authorise the link from within Xero — Lottie never sees or stores your Xero password, and you can disconnect at any time."],
      ["Will Lottie change my Xero data without my approval?", "No. You stay in control. Journals can be posted automatically or held behind a two-person approval, and nothing is posted to Xero without following the rules you set."],
      ["Is my data secure?", <>Yes. Connections use Xero's encrypted OAuth standard, data is transmitted over secure HTTPS, and every action in the app is recorded in a full audit trail. You can revoke access from Xero or disconnect in-app whenever you like. <Link to="/security" className="font-semibold" style={{ color: "#F37A24" }}>Read our full security &amp; data-handling overview →</Link></>],
      ["Can I connect more than one organisation?", "Absolutely. Lottie is built for portfolios — connect as many Xero organisations as you need and manage them all from one portal, each with its own isolated chart of accounts and settings."],
    ],
  },
  {
    tag: "Features",
    faqs: [
      ["What does Lottie automate?", "Accruals, prepayments and deferred income (revenue recognition). She identifies the relevant transactions in Xero, builds the release schedules, posts the monthly journals and reversals, and maintains your registers — so month-end is faster, more accurate and far less manual."],
      ["Does it handle recurring accruals?", "Yes. Lottie's recurring accrual engine automatically accrues and reverses repeating bills each period, and intelligently matches them when the actual bill arrives."],
      ["Can I see a forecast?", "Yes — Lottie gives you a 12-month forecast of opening balances, monthly releases and closing positions across every category and entity."],
      ["Can I make manual adjustments?", "Of course. Alongside the automation you can create one-off or opening-balance schedules by hand, with the same release logic applied."],
      ["Can I export reports?", "Yes — your registers, forecasts, reconciliations and month-end close packs export to branded PDF and Excel in a click."],
    ],
  },
  {
    tag: "Pricing & trial",
    faqs: [
      ["How much does it cost?", "£39 per connected Xero organisation, per month. As you add organisations a volume discount applies across your whole subscription — up to 69% — and paying annually gives you 2 months free. For 51+ organisations we offer bespoke Enterprise pricing."],
      ["Is there a free trial?", "Yes — every account starts with a 7-day free trial, and no card is required to begin. You'll only add payment details if you choose to keep Lottie after the trial, and you can cancel any time."],
      ["Can I cancel anytime?", "Yes. There are no long-term contracts — cancel whenever you like and you keep access until the end of your billing period."],
      ["What happens if I add or remove an organisation?", "Your subscription updates to reflect the new organisation count and the volume discount is recalculated automatically on your next bill."],
    ],
  },
  {
    tag: "Support",
    faqs: [
      ["How do I get help?", "We're here whenever you need us — email hello@workwithlottie.com and a real person will help you get set up or answer any questions."],
      ["Do you help with onboarding?", "Yes. Connecting Xero and configuring your codes is quick, and our team is on hand to make sure your registers are set up correctly from day one."],
    ],
  },
];

export default function FAQ() {
  // FAQ answers can be strings or JSX — flatten to plain text for valid FAQPage structured data.
  const nodeToText = (node) => {
    if (node == null || typeof node === "boolean") return "";
    if (typeof node === "string" || typeof node === "number") return String(node);
    if (Array.isArray(node)) return node.map(nodeToText).join("");
    if (isValidElement(node)) return Children.toArray(node.props.children).map(nodeToText).join("");
    return "";
  };
  const answerText = (a) => nodeToText(a).replace(/\s*→\s*$/, "").replace(/\s+/g, " ").trim();

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: GROUPS.flatMap((g) =>
      g.faqs.map(([q, a]) => ({
        "@type": "Question",
        name: q,
        acceptedAnswer: { "@type": "Answer", text: answerText(a) },
      }))),
  };
  return (
    <MarketingLayout>
      <Seo title="FAQ" path="/faq"
        description="Answers about WorkWithLottie: how Lottie connects to Xero, what she automates (accruals, prepayments, deferred income), data security, pricing and the free trial."
        jsonLd={faqJsonLd} />
      <section className="max-w-3xl mx-auto px-6 pt-16 pb-10 text-center">
        <div className="overline mb-4" style={{ color: "#F37A24" }}>FAQ</div>
        <h1 className="font-heading font-black text-navy text-4xl sm:text-5xl tracking-tighter">
          Questions, answered
        </h1>
        <p className="text-navy/70 text-base sm:text-lg mt-5 leading-relaxed">
          Everything you need to know about Lottie, Xero and how month-end works. Can't find what
          you're after? <a href="mailto:hello@workwithlottie.com" className="font-semibold" style={{ color: "#F37A24" }}>Get in touch</a>.
        </p>
      </section>

      <section className="max-w-3xl mx-auto px-6 space-y-12">
        {GROUPS.map((g) => (
          <div key={g.tag} data-testid={`faq-group-${g.tag}`}>
            <h2 className="font-heading font-bold text-navy text-xl mb-3">{g.tag}</h2>
            <Accordion type="single" collapsible className="bg-white rounded-2xl border border-slate-200 shadow-card px-5">
              {g.faqs.map(([q, a], i) => (
                <AccordionItem key={q} value={`${g.tag}-${i}`} className="border-slate-100 last:border-0"
                  data-testid={`faq-item-${g.tag}-${i}`}>
                  <AccordionTrigger className="font-heading font-semibold text-navy text-base hover:no-underline py-5">
                    {q}
                  </AccordionTrigger>
                  <AccordionContent className="text-navy/70 text-sm leading-relaxed pb-5">
                    {a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        ))}
      </section>

      <section className="max-w-6xl mx-auto px-6 py-20 text-center">
        <h2 className="font-heading font-bold text-navy text-3xl sm:text-4xl tracking-tight">Ready to simplify month-end?</h2>
        <p className="text-navy/70 mt-4 max-w-xl mx-auto">Connect Xero and let Lottie do the heavy lifting — free for 7 days.</p>
        <div className="flex flex-wrap justify-center gap-3 mt-8">
          <Link to="/start" data-testid="faq-cta-trial" className="text-white font-semibold px-6 py-3.5 rounded-xl flex items-center gap-2" style={{ background: "#F37A24" }}>
            Start free trial <ArrowRight size={18} weight="bold" />
          </Link>
          <Link to="/pricing" data-testid="faq-cta-pricing" className="bg-white text-navy font-semibold px-6 py-3.5 rounded-xl border border-slate-200 hover:border-slate-300 transition-colors">
            View pricing
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
}
